// client/src/pages/admin/AdminFlashOffers.js
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../services/api';
import axios from 'axios';
import { Zap, XCircle, Clock, CheckCircle, Package } from 'lucide-react';
import ConfirmModal from '../../components/common/ConfirmModal';

const AdminFlashOffers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') navigate('/admin/login');
  }, [user, navigate]);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await axios.get(`${API_URL}/admin/platform/flash-offers?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(res.data.offers || []);
    } catch (err) {
      console.error('Fetch offers error:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/admin/platform/flash-offers/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data.stats);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleDeactivate = async (offerId) => {
    setConfirmModal({
      title: 'Deactivate Flash Offer',
      message: 'Deactivate this flash offer?',
      icon: 'warning',
      confirmLabel: 'Deactivate',
      confirmColor: 'red',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const token = localStorage.getItem('token');
          await axios.put(`${API_URL}/admin/platform/flash-offers/${offerId}/deactivate`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          fetchOffers();
          fetchStats();
        } catch (err) {
          toast.error('Failed to deactivate');
        }
      }
    });
  };

  const getStatusBadge = (offer) => {
    if (offer.status === 'cancelled') return { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' };
    if (offer.isExpired) return { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Expired' };
    if (offer.isActive) return { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' };
    return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Scheduled' };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Zap className="w-6 h-6 text-orange-500" /> Flash Offers Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">Monitor and manage all flash offers across wholesalers</p>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">Total Offers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">Active Now</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">Expired</p>
                <p className="text-2xl font-bold text-gray-500">{stats.expired}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500">Cancelled</p>
                <p className="text-2xl font-bold text-red-600">{stats.cancelled}</p>
              </div>
            </div>
          )}

          {/* Filter */}
          <div className="flex gap-2 mb-4">
            {['all', 'active', 'cancelled'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  statusFilter === s ? 'bg-blue-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'
                }`}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          {/* Offers List */}
          <div className="bg-white rounded-lg border">
            {loading ? (
              <div className="p-8 flex justify-center"><LoadingSpinner /></div>
            ) : offers.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Zap className="w-12 h-12 mx-auto mb-2 text-gray-200" />
                No flash offers found
              </div>
            ) : (
              <div className="divide-y">
                {offers.map((offer) => {
                  const badge = getStatusBadge(offer);
                  return (
                    <div key={offer._id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">{offer.title}</h3>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
                              {badge.label}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mb-2">
                            {offer.wholesalerId?.companyName || 'Unknown Wholesaler'} • {offer.itemCount} products
                          </p>
                          <div className="flex items-center gap-4 text-xs text-gray-400">
                            <span><Clock className="w-3 h-3 inline mr-1" />
                              {new Date(offer.startDate).toLocaleDateString('el-GR')} — {new Date(offer.endDate).toLocaleDateString('el-GR')}
                            </span>
                            {offer.stats && (
                              <>
                                <span>Views: {offer.stats.views || 0}</span>
                                <span>Orders: {offer.stats.ordersGenerated || 0}</span>
                              </>
                            )}
                          </div>
                        </div>
                        {offer.isActive && offer.status !== 'cancelled' && (
                          <button onClick={() => handleDeactivate(offer._id)}
                            className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center gap-1">
                            <XCircle className="w-4 h-4" /> Deactivate
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
      <Footer />

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default AdminFlashOffers;